import React, { useCallback, useEffect } from "react";
import SafeNumberInput from "../SafeNumberInput";
import { api } from "../utils/api";
import { useNavigate, Link } from "react-router-dom";
import { useReadAloud, ReadableItem } from "../utils/useReadAloud";
import { ReadAloudBar } from "../utils/ReadAloudBar";
import { HighlightedText, Mark } from "../utils/HighlightedText";
import { cleanTextForSpeech } from "../utils/speechText";

interface ITweet {
  url: string;
  source: string;
  published_at: number;
  content: string;
  content_marks: Mark[];
  read: number;
}

interface ITweetsRequest {
  query: string;
  start_date: string;
  end_date: string;
  source: string;
  page: number;
  table: string;
  saved_only: boolean;
  reverse: boolean;
  has_tickers: boolean;
}

export const MobileTweetsSearch = () => {
  const [tweets, setTweets] = React.useState<ITweet[]>([]);
  const [tweetsRequest, setTweetsRequest] = React.useState<ITweetsRequest>({
    query: "",
    start_date: "",
    end_date: "",
    page: 1,
    source: "",
    table: "tweets",
    saved_only: false,
    reverse: false,
    has_tickers: false,
  });
  const [errorMessage, setErrorMessage] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);

  const navigate = useNavigate();

  const readAloud = useReadAloud([tweets]);

  const readableItems: ReadableItem[] = tweets
    .filter((item) => item.read === 0)
    .map((tweet) => ({ key: tweet.url, texts: [cleanTextForSpeech(tweet.content).toLowerCase()] }));

  const onItemDone = useCallback((key: string) => {
    markTweet(key, 1);
  }, [tweets]);

  const getTweets = async () => {
    setErrorMessage("");
    setIsLoading(true);

    try {
      const { data } = await api.post<ITweet[]>("/search", tweetsRequest);
      setTweets(data);
    } catch (error: any) {
      console.error(error?.message ?? error);
      setErrorMessage("Error while fetching tweets");
    } finally {
      setIsLoading(false);
    }
  };

  function unixTimestampToNewYork(timestamp: number): [string, Date] {
    const date = new Date(timestamp * 1000);
    // convert to New York time, format as 17 Apr 2025 HH:MM
    const newyorkDate: Date = new Date(
      date.toLocaleString("en-US", { timeZone: "America/New_York" })
    );

    const day = String(newyorkDate.getDate()).padStart(2, "0");
    const month = String(newyorkDate.getMonth() + 1).padStart(2, "0");
    const year = newyorkDate.getFullYear();
    const hours = String(newyorkDate.getHours()).padStart(2, "0");
    const minutes = String(newyorkDate.getMinutes()).padStart(2, "0");
    const seconds = String(newyorkDate.getSeconds()).padStart(2, "0");

    // get the day of the week
    const options: Intl.DateTimeFormatOptions = { weekday: "short" };
    const dayOfWeek = newyorkDate.toLocaleDateString("en-US", options);

    return [
      `${year}-${month}-${day} ${hours}:${minutes} (${dayOfWeek})`,
      newyorkDate,
    ];
  }

  const markTweet = (url: string, read: number) => {
    const newMark = read % 3; // 0 = not read, 1 = read, 2 = bookmarked

    setTweets((prevTweets) =>
      prevTweets.map((item) =>
        item.url === url ? { ...item, read: newMark } : item
      )
    );

    api
      .post("/mark", { url, read: newMark, table: "tweets" })
      .then((response) => {
        const data = response.data;
        if (!data?.success) {
          console.error("data.success is false");
          // revert the change
          setTweets((prevTweets) =>
            prevTweets.map((item) =>
              item.url === url ? { ...item, read: read } : item
            )
          );
          throw new Error("Failed to mark news as read");
        }
      })
      .catch((error) => {
        // revert the change
        setTweets((prevTweets) =>
          prevTweets.map((item) =>
            item.url === url ? { ...item, read: read } : item
          )
        );
        console.error("Error marking news as read:", error);
      });
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-auto p-2">
        <Link to="/mobile" className="btn btn-default flex-grow">To Menu</Link>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          getTweets();
        }}
        className="flex flex-col mb-4"
      >
        <div className="form-group">
          <label htmlFor="page">Page:</label>
          <SafeNumberInput
            id="page"
            value={tweetsRequest.page}
            onChange={(value) =>
              setTweetsRequest({ ...tweetsRequest, page: value })
            }
            min={1}
            max={10000}
          />
        </div>

        <div className="form-group flex flex-row gap-2">
          <div>
            <label htmlFor="query">Query:</label>
            <input
              type="text"
              id="query"
              value={tweetsRequest.query}
              onChange={(e) =>
                setTweetsRequest({ ...tweetsRequest, query: e.target.value })
              }
            />
          </div>

          <div>
            <label htmlFor="query-template">Template:</label>
            <select
              id="query-template"
              value={tweetsRequest.query}
              onChange={(e) => {
                setTweetsRequest({ ...tweetsRequest, query: e.target.value });
              }}
              className="border border-gray-300 rounded p-1 block w-full"
            >
              <option value="">None</option>
              <option value="!(target | revenue | attached)">Corleone</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="start-date">Start date:</label>
          <input
            placeholder="YYYY-MM-DD"
            type="text"
            id="start-date"
            value={tweetsRequest.start_date}
            onChange={(e) =>
              setTweetsRequest({
                ...tweetsRequest,
                start_date: e.target.value,
              })
            }
          />
        </div>
        <div className="form-group">
          <label htmlFor="end-date">End date:</label>
          <input
            placeholder="YYYY-MM-DD"
            type="text"
            id="end-date"
            value={tweetsRequest.end_date}
            onChange={(e) =>
              setTweetsRequest({ ...tweetsRequest, end_date: e.target.value })
            }
          />
        </div>
        <div className="form-group">
          <label htmlFor="source">Source:</label>
          <select
            id="source"
            value={tweetsRequest.source}
            onChange={(e) => {
              setTweetsRequest({ ...tweetsRequest, source: e.target.value });
            }}
            className="border border-gray-300 rounded p-1 block w-full"
          >
            <option value="">All</option>
              <option value="FirstSquawk">FirstSquawk</option>
              <option value="TheInsiderPaper">TheInsiderPaper</option>
              <option value="notreload_ai">notreload_ai</option>
              <option value="Trade_The_News">Trade_The_News</option>
              <option value="LiveSquawk">LiveSquawk</option>
              <option value="CorleoneDon77">CorleoneDon77</option>
              <option value="wallstengine">wallstengine</option>
              <option value="DeItaone">DeItaone</option>
              <option value="OracleNYSE">OracleNYSE</option>
              <option value="tenet_research">tenet_research</option>
              <option value="MarketNews_Feed">MarketNews_Feed</option>
              <option value="CryptoMilox">CryptoMilox</option>
              <option value="SpacRight">SpacRight</option>
              <option value="LF94Trades">LF94Trades</option>
              <option value="faststocknewss">faststocknewss</option>
              <option value="blckchaindaily">blckchaindaily</option>
              <option value="NuntioBot">NuntioBot</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="saved_only">
            <input
              type="checkbox"
              id="saved_only"
              checked={tweetsRequest.saved_only}
              onChange={(e) =>
                setTweetsRequest({
                  ...tweetsRequest,
                  saved_only: e.target.checked,
                })
              }
            />
            &nbsp; Saved only
          </label>
        </div>

        <div className="form-group">
          <label htmlFor="has_tickers">
            <input
              type="checkbox"
              id="has_tickers"
              checked={tweetsRequest.has_tickers}
              onChange={(e) =>
                setTweetsRequest({
                  ...tweetsRequest,
                  has_tickers: e.target.checked,
                })
              }
            />
            &nbsp; Has tickers
          </label>
        </div>

        <button type="submit" className="btn btn-default">
          Search
        </button>
      </form>

      <TweetsTable
        tweets={tweets}
        errorMessage={errorMessage}
        isLoading={isLoading}
        searchTerm={tweetsRequest.query}
        unixTimestampToNewYork={unixTimestampToNewYork}
        markTweet={markTweet}
        currentlyReadingKey={readAloud.currentlyReadingKey}
      />
      </div>

      <ReadAloudBar
        voiceControls={readAloud.voiceControls}
        setVoiceControls={readAloud.setVoiceControls}
        toggleReading={readAloud.toggleReading}
        skipItem={readAloud.skipItem}
        items={readableItems}
        onItemDone={onItemDone}
        idPrefix="mobile-tweets-"
      />
    </div>
  );
};

const TweetsTable = (props: {
  tweets: ITweet[];
  errorMessage: string;
  isLoading: boolean;
  searchTerm: string;
  unixTimestampToNewYork: (timestamp: number) => [string, Date];
  markTweet: (url: string, read: number) => void;
  currentlyReadingKey: string;
}) => {
  const [highlightedText, setHighlightedText] = React.useState<string[]>([]);

  useEffect(() => {
    // if multi word, pass array of words
    const searchTerm = props.searchTerm.trim();

    if (searchTerm) {
      setHighlightedText(searchTerm.split(/\s+/));
    } else {
      setHighlightedText([]);
    }
  }, [props.isLoading]);

  if (props.isLoading) {
    return <div className="text-center">Loading...</div>;
  }

  if (props.errorMessage !== "") {
    return <p className="text-center">{props.errorMessage}</p>;
  }

  if (props.tweets.length === 0) {
    return <p className="text-center">No tweets found</p>;
  }

  function addMarkClass(mark: number): string {
    switch (mark) {
      case 0:
        return "bg-gray-400";
      case 1:
        return "bg-blue-600";
      case 2:
        return "bg-yellow-600";
      default:
        return "bg-gray-400";
    }
  }

  return (
    <div>
      {props.tweets.map((tweet) => (
        <div key={tweet.url} className={`flex gap-4 mb-4 ${props.currentlyReadingKey === tweet.url ? "bg-yellow-100" : ""}`}>
          <div
            className={`min-w-4 cursor-pointer ${addMarkClass(tweet.read)}`}
            onClick={() => {
              props.markTweet(tweet.url, tweet.read + 1);
            }}
          ></div>

          <div>
            <p className="text-gray-500 flex flex-col">
              <a href={tweet.url} target="_blank" rel="noopener noreferrer">
                {tweet.source}
              </a>

              <span
                onClick={(e) => {
                  e.preventDefault();
                  props.markTweet(tweet.url, tweet.read + 1);
                }}
              >
                {props.unixTimestampToNewYork(tweet.published_at)[0]}
              </span>
            </p>
            <div className="whitespace-pre-wrap">
              <HighlightedText text={tweet.content} marks={tweet.content_marks} />
            </div>
          </div>

          <hr />
        </div>
      ))}
    </div>
  );
};
